import { memo } from 'react';
import PropTypes from 'prop-types';
import { FaCheck } from 'react-icons/fa';
import getTourActivityIcon from '../../../../../helpers/getTourActivityIcon';

const Section = ({ title, children }) => (
  <div className="mb-6">
    <div className="text-lg md:text-xl font-semibold font-subHeading mb-2">{title}</div>
    {children}
  </div>
);

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const toHighlightLines = (text) =>
  (text || '')
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*[-•*]\s*/, '').trim())
    .filter(Boolean);

const isContinuedStop = (stop) => (stop.title || '').trim().toLowerCase() === 'continued';

const toStopText = (text) =>
  (text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .join(' ');

const toParagraphs = (text) =>
  (text || '')
    .split(/\r?\n\s*\r?\n|\r?\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

const OverviewDetails = ({ itinerary, product }) => {
  const highlightLines = toHighlightLines(product?.highlights);
  const descriptionParagraphs = toParagraphs(product?.description || itinerary.tour?.description);
  const itineraryText = product?.itineraryText;
  const stops = product?.stops || [];
  const activities = product?.activities || itinerary.tour?.activities || [];

  return (
    <div className="text-sm">
      {activities.length > 0 && (
        <Section title="Activities">
          {activities.map((activity) => {
            const Icon = getTourActivityIcon(activity);
            const [type, ...place] = activity.split(' at ');
            return (
              <div key={activity} className="flex items-center text-base text-gray-700 py-1">
                <Icon className="mr-2 text-gray-500 flex-shrink-0" />
                <span>
                  {type}
                  {place.length > 0 && <span> at {place.join(' at ')}</span>}
                </span>
              </div>
            );
          })}
        </Section>
      )}

      {highlightLines.length > 0 && (
        <Section title="Highlights">
          <div className="md:columns-2 lg:columns-3 md:gap-x-8">
            {highlightLines.map((line) => (
              <div
                key={line}
                className="flex items-start text-base text-gray-700 leading-snug py-1 break-inside-avoid"
              >
                <FaCheck className="mr-2 mt-1 text-theme flex-shrink-0" size="12" />
                <span>{line}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {descriptionParagraphs.length > 0 && (
        <Section title="Description">
          {descriptionParagraphs.map((paragraph) => (
            <p key={paragraph} className="text-base text-gray-700 leading-snug text-justify mb-2">
              {paragraph}
            </p>
          ))}
        </Section>
      )}

      {stops.length > 0 && (
        <Section title="Itinerary">
          <div className="overflow-x-auto">
            <table className="w-full text-base border-collapse">
              <tbody>
                {stops.map((stop, index) => (
                  <tr key={`${index}-${stop.title}`} className="border-b last:border-b-0 align-top">
                    <td className="py-2 pr-4 font-medium whitespace-nowrap">
                      {isContinuedStop(stop) ? '' : stop.title}
                    </td>
                    <td className="py-2 text-gray-700">{toStopText(stop.description)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {!stops.length && itineraryText && (
        <Section title="Itinerary">
          {toParagraphs(itineraryText).map((paragraph) => (
            <p key={paragraph} className="text-base text-gray-700 leading-snug text-justify mb-2">
              {paragraph}
            </p>
          ))}
        </Section>
      )}
    </div>
  );
};

OverviewDetails.propTypes = {
  itinerary: PropTypes.shape().isRequired,
  product: PropTypes.shape(),
};

OverviewDetails.defaultProps = {
  product: undefined,
};

export default memo(OverviewDetails);
